import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { redirect, useLocation } from "react-router-dom";
import axios from "axios";
import { X } from "lucide-react";

const BackendUrl  = "https://amrs-prod.eu-north-1.elasticbeanstalk.com"



const PaymentCallback = () => {
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Verifying your payment...");
  const [value, setValue] = useState('');
  const location = useLocation();
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 10;
  const effectRan = useRef(false);

  useEffect(() => {
    if (effectRan.current) return;
    effectRan.current = true;

    const reference = localStorage.getItem("amrsghana_payRef", )
    

    if (!reference) {
      setStatus("error");
      setMessage("Missing payment reference. Verify manually or call support after multiple trials.");
      return;
    }

    let attemptCount = 0;
    const MAX_COUNT = 10;

    const checkPaymentStatus = async () => {
      try {
        attemptCount++;
        setAttempts(attemptCount);

        const response = await axios.get(
          `${BackendUrl}/event/amrsghana/verify-payment-state/${reference}`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        const data = response.data;
        const paymentStatus = data.payment_state;

    switch (paymentStatus) {
      case "success":
        setStatus("success");
        setMessage("Payment was successful! Redirecting...");
        localStorage.removeItem("amrsghana_payRef");
        setTimeout(() => (window.location.href = "/iumrs-icam25"), 5000);
        break;

      case "pending":
      case "processing":
      case "queued":
        if (attemptCount < MAX_COUNT) {
          setMessage("Your payment is still processing. Retrying...");
          setTimeout(checkPaymentStatus, 3000);
        } else {
          setStatus("error");
          setMessage("Payment verification timed out. Please retry or contact support.");
        }
        break;

      case "ongoing":
        setStatus("ongoing")
        setMessage("Complete any required steps to finalize your transaction")

      case "abandoned":
        setStatus("abandoned");
        setMessage("Payment was abandoned. Please complete your transaction");
        localStorage.removeItem("amrsghana_payRef");
        break;

      case "failed":
        setStatus("failed");
        setMessage("Payment failed. Initiate payment process and try again.");
        localStorage.removeItem("amrsghana_payRef");
        break;

      case "reversed":
        setStatus("reversed");
        setMessage("Payment was reversed. If this was an error, contact support.");
        localStorage.removeItem("amrsghana_payRef");
        break;

      default:
        setStatus("error");
        setMessage("Unexpected payment status. Please contact support.");
        break;
    }
      } catch (error) {
        if (error.response && error.response.request.status == 500){
          setStatus("error")
          setMessage("Error verifying payment. Reference code not valid")
          localStorage.removeItem("amrsghana_payRef");

        }
        else if (attemptCount < MAX_COUNT) {
          setStatus("verifying"); 
          setMessage(`Error verifying payment. Retrying in 5 seconds...`);
          setTimeout(checkPaymentStatus, 5000);
        } else {
          setStatus("error");
          setMessage(`Error verifying payment after multiple attempts. Refresh page or try again later.`);
        }
    }
  };

    checkPaymentStatus();
    
    return () => {
      effectRan.current = false;
    };
  }, [location]);

  return (
    <Container>
      <Card>
        <button className="cancel-button"
          style={{color: "rgba(233, 31, 31, 1)", position:"absolute", right:"2rem", top:"2rem", borderRadius: "5px", borderColor: "rgba(233, 31, 31, 1)",backgroundColor:"#f4f6f9"}}
          onClick={() => (window.location.href = "/")}
          >
          <X size={20} /> 
        </button>
        <Title>Payment Verification</Title>
        {status === "verifying" && (
          <LoadingSpinner>
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#3498db"
                strokeWidth="5"
                strokeDasharray="94.2"
                strokeDashoffset="0"
              />
            </svg>
            <p>{message}</p>
            <p>Attempt {attempts} of {MAX_ATTEMPTS}</p>
          </LoadingSpinner>
        )}
        {status === "success" && (
          <SuccessMessage>
            <h3>✓ Success!</h3>
            <p>{message}</p>
          </SuccessMessage>
        )}
        {status === "ongoing" && (
          <ErrorMessage>
            <h3>⌛ Ongoing</h3>
            <p>{message}</p>
            <Button>
              <button onClick={() => (window.location.reload())}>
              Verify again
              </button>
            </Button>
          </ErrorMessage>
        )}

        {status === "abandoned" && (
          <ErrorMessage>
            <h3>⚠ Abandoned</h3>
            <p>{message}</p>
            <Button>
              <button onClick={() => (window.location.reload())}>
                Verify Again
              </button>
              <button onClick={() => (window.location.href = "/event/payment-initializer")}>
                Initialize payment
              </button>
            </Button>
          </ErrorMessage>
        )}

        {status === "failed" && (
          <ErrorMessage>
            <h3>❌ Failed</h3>
            <p>{message}</p>
            <Button>
              <button onClick={() => (window.location.href = "/event/payment-initializer")}>
                Retry Payment
              </button>
            </Button>
          </ErrorMessage>
        )}

        {status === "reversed" && (
          <ErrorMessage>
            <h3>🔄 Reversed</h3>
            <p>{message}</p>
            <Button>
              <button onClick={() => (window.location.href = "/iumrs-icam25")}>
                Contact Support
              </button>
            </Button>
          </ErrorMessage>
        )}

        {status === "processing" && (
          <ErrorMessage>
            <h3>🔄 Processing</h3>
            <p>{message}</p>
            <Button>
              <button onClick={() => (window.location.href = "/iumrs-icam25")}>
                Contact Support
              </button>
            </Button>
          </ErrorMessage>
        )}
        {status === "error" && (
          <ErrorMessage>
            <h3>⚠ Error</h3>
            <p>{message}</p>
            <Button>
              <button onClick={() => setStatus('manual-verify')}>
                Verify manually
              </button>
              <button onClick={() => (window.location.href = "/event/payment-initializer")}>
                Initialize payment
              </button>
            </Button>
          </ErrorMessage>
        )}
        { status === "manual-verify" && (
          <Form onSubmit={(e) => {
            e.preventDefault();
            localStorage.setItem('amrsghana_payRef', value);
            window.location.reload();
          }}>
            <p style={{color: "rgb(177, 34, 34)"}}>Please enter the reference code on the transaction receipt sent to your email.</p>
            <input type="text" value={value} onChange={(e) => setValue(e.target.value)}
              placeholder="IUMRS_ICAM25XXXXXXXX" required
            />
            <Button> 
              <button>
                Verify
              </button>
            </Button>
          </Form>
        )

        }
      </Card>
    </Container>
  );
};

const Form = styled.form`
    display: block;
    margin-top: 1em;
`;





const Button = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  gap: 1.3rem;

  button {
    background-color: #3498db;
    color: white;
    border: none;
    padding: 0.7rem 1.2rem;
    min-width: 7rem;
    border-radius: 5px;
    cursor: pointer;
    transition: background 0.3s ease-in-out;
    
    &:hover {
      background-color: #2980b9;
    }
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 90vh;
  background-color: #f4f6f9;
  padding: 2rem;

  input{
    margin-top: 1rem;
    padding: 0.8em;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    width: 15rem; 
    color: #4d3333ff; 
  }

  input::placeholder{
      color: #b39b9bff;
      font-size: 0.8rem;
      text-align: center;
      opacity: 0.7
    }

  .cancel-button:hover {
          transform: scale(1.05);
        }
`;

const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const Title = styled.h2`
  color: #2c3e50;
  margin-bottom: 1.5rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  svg {
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1rem;
    color: #555;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  color: #27ae60;
  text-align: center;

  h3 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
  }
`;

const ErrorMessage = styled.div`
  color: #c0392b;
  text-align: center;

  h3 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
  
  
`;



export default PaymentCallback;
